import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CountrySelector } from '@/components/country-selector'
import { useEditOrderAddress } from '@/lib/api/orders'

const addressFormSchema = z.object({
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	company: z.string().optional(),
	address1: z.string().min(1),
	address2: z.string().optional(),
	city: z.string().min(1),
	state: z.string().optional(),
	country: z.string().min(1),
	postal: z.string().min(1),
	phone: z.string().optional(),
	email: z.string().optional(),
})

const editAddressFormSchema = z.object({
	shippingAddress: addressFormSchema,
	billingAddress: addressFormSchema,
})

type EditAddressFormData = z.infer<typeof editAddressFormSchema>

const EMPTY_ADDRESS: z.infer<typeof addressFormSchema> = {
	firstName: '',
	lastName: '',
	company: '',
	address1: '',
	address2: '',
	city: '',
	state: '',
	country: '',
	postal: '',
	phone: '',
	email: '',
}

const normalizeAddress = (address: any): z.infer<typeof addressFormSchema> => {
	if (!address) return EMPTY_ADDRESS
	return {
		firstName: address.firstName ?? '',
		lastName: address.lastName ?? '',
		company: address.company ?? '',
		address1: address.address1 ?? '',
		address2: address.address2 ?? '',
		city: address.city ?? '',
		state: address.state ?? address.province ?? '',
		country: address.country ?? '',
		postal: address.postal ?? address.zip ?? '',
		phone: address.phone ?? '',
		email: address.email ?? '',
	}
}

interface AddressFieldsProps {
	prefix: 'shippingAddress' | 'billingAddress'
	register: ReturnType<typeof useForm<EditAddressFormData>>['register']
	errors: ReturnType<typeof useForm<EditAddressFormData>>['formState']['errors']
	watch: ReturnType<typeof useForm<EditAddressFormData>>['watch']
	setValue: ReturnType<typeof useForm<EditAddressFormData>>['setValue']
}

function AddressFields({ prefix, register, errors, watch, setValue }: AddressFieldsProps) {
	const { t } = useTranslation('orders')
	const fieldErrors = errors[prefix]

	return (
		<div className="grid grid-cols-2 gap-4">
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.firstName`}>{t('firstName')}</Label>
				<Input
					id={`${prefix}.firstName`}
					{...register(`${prefix}.firstName`)}
					error={!!fieldErrors?.firstName}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.lastName`}>{t('lastName')}</Label>
				<Input
					id={`${prefix}.lastName`}
					{...register(`${prefix}.lastName`)}
					error={!!fieldErrors?.lastName}
				/>
			</div>
			<div className="col-span-2 space-y-1.5">
				<Label htmlFor={`${prefix}.company`}>{t('company')}</Label>
				<Input
					id={`${prefix}.company`}
					{...register(`${prefix}.company`)}
				/>
			</div>
			<div className="col-span-2 space-y-1.5">
				<Label htmlFor={`${prefix}.address1`}>{t('address1')}</Label>
				<Input
					id={`${prefix}.address1`}
					{...register(`${prefix}.address1`)}
					error={!!fieldErrors?.address1}
				/>
			</div>
			<div className="col-span-2 space-y-1.5">
				<Label htmlFor={`${prefix}.address2`}>{t('address2')}</Label>
				<Input
					id={`${prefix}.address2`}
					{...register(`${prefix}.address2`)}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.city`}>{t('city')}</Label>
				<Input
					id={`${prefix}.city`}
					{...register(`${prefix}.city`)}
					error={!!fieldErrors?.city}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.state`}>{t('state')}</Label>
				<Input
					id={`${prefix}.state`}
					{...register(`${prefix}.state`)}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.country`}>{t('country')}</Label>
				<CountrySelector
					value={watch(`${prefix}.country`)}
					onChange={(value) => setValue(`${prefix}.country`, value, { shouldValidate: true })}
				/>
				{fieldErrors?.country && (
					<p className="text-destructive text-sm">{fieldErrors.country.message}</p>
				)}
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.postal`}>{t('postal')}</Label>
				<Input
					id={`${prefix}.postal`}
					{...register(`${prefix}.postal`)}
					error={!!fieldErrors?.postal}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.phone`}>{t('phone')}</Label>
				<Input
					id={`${prefix}.phone`}
					{...register(`${prefix}.phone`)}
				/>
			</div>
			<div className="space-y-1.5">
				<Label htmlFor={`${prefix}.email`}>{t('email')}</Label>
				<Input
					id={`${prefix}.email`}
					type="email"
					{...register(`${prefix}.email`)}
				/>
			</div>
		</div>
	)
}

interface EditAddressDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	orderId: string
	shippingAddress: any
	billingAddress: any
}

export function EditAddressDialog({
	open,
	onOpenChange,
	orderId,
	shippingAddress,
	billingAddress,
}: EditAddressDialogProps) {
	const { t } = useTranslation('orders')
	const editAddressMutation = useEditOrderAddress()

	const {
		register,
		handleSubmit,
		formState: { errors },
		watch,
		setValue,
		reset,
	} = useForm<EditAddressFormData>({
		resolver: zodResolver(editAddressFormSchema),
		defaultValues: {
			shippingAddress: normalizeAddress(shippingAddress),
			billingAddress: normalizeAddress(billingAddress),
		},
	})

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!nextOpen) {
				reset({
					shippingAddress: normalizeAddress(shippingAddress),
					billingAddress: normalizeAddress(billingAddress),
				})
			}
			onOpenChange(nextOpen)
		},
		[onOpenChange, reset, shippingAddress, billingAddress],
	)

	const onSubmit = useCallback(
		(data: EditAddressFormData) => {
			editAddressMutation.mutate(
				{
					orderId,
					shippingAddress: data.shippingAddress,
					billingAddress: data.billingAddress,
				},
				{ onSuccess: () => handleOpenChange(false) },
			)
		},
		[orderId, editAddressMutation, handleOpenChange],
	)

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t('editAddress')}</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Tabs defaultValue="shipping" className="w-full">
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="shipping">{t('shipping')}</TabsTrigger>
							<TabsTrigger value="billing">{t('billing')}</TabsTrigger>
						</TabsList>
						<TabsContent value="shipping" className="mt-4">
							<AddressFields
								prefix="shippingAddress"
								register={register}
								errors={errors}
								watch={watch}
								setValue={setValue}
							/>
						</TabsContent>
						<TabsContent value="billing" className="mt-4">
							<AddressFields
								prefix="billingAddress"
								register={register}
								errors={errors}
								watch={watch}
								setValue={setValue}
							/>
						</TabsContent>
					</Tabs>
					<DialogFooter className="mt-6">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
						>
							{t('common:cancel', { ns: 'common' })}
						</Button>
						<Button type="submit" disabled={editAddressMutation.isPending}>
							{editAddressMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t('common:save', { ns: 'common' })}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
